import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import AppLayout from '@/components/layout/AppLayout';
import Login from '@/pages/Login/Login';
import ExerciseImport from '@/pages/admin/ExerciseImport/ExerciseImport';
import Register from '@/pages/Register/Register';
import ForgotPassword from '@/pages/ForgotPassword/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword/ResetPassword';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminTrainers from '@/pages/admin/Trainers';
import AdminUsers from '@/pages/admin/Users';
import AdminExercises from '@/pages/admin/Exercises';
import AdminEquipments from '@/pages/admin/Equipments';
import EquipmentImport from '@/pages/admin/EquipmentImport';
import AdminMarketplace from '@/pages/admin/Marketplace';
import AdminCoupons from '@/pages/admin/Coupons';
import AdminFinance from '@/pages/admin/Finance';
import AdminInvoices from '@/pages/admin/Invoices';
import AdminAudit from '@/pages/admin/Audit';
import AdminThemes from '@/pages/admin/Themes';
import AdminReviews from '@/pages/admin/Reviews/Reviews';
import TrainerDashboard from '@/pages/trainer/Dashboard';
import TrainerStudents from '@/pages/trainer/Students';
import TrainerAgenda from '@/pages/trainer/Agenda';
import TrainerLibrary from '@/pages/trainer/Library';
import TrainerEquipments from '@/pages/trainer/Equipments';
import TrainerWorkouts from '@/pages/trainer/Workouts';
import TrainerTemplates from '@/pages/trainer/Templates';
import TrainerPackages from '@/pages/trainer/Packages';
import TrainerChat from '@/pages/trainer/Chat';
import TrainerProfile from '@/pages/trainer/Profile';
import TrainerEvolution from '@/pages/trainer/Evolution';
import TrainerInvoices from '@/pages/trainer/Invoices';
import Appearance from '@/pages/Settings/Appearance';
import Account from '@/pages/Settings/Account';
import TrainerPage from '@/pages/public/TrainerPage/TrainerPage';
import PurchaseStatus from '@/pages/public/PurchaseStatus/PurchaseStatus';
import AcceptInvite from '@/pages/public/AcceptInvite/AcceptInvite';

// O web só tem área pra admin e personal; aluno usa o app.
const WEB_ROLES = ['admin', 'trainer'];

// Guard: exige autenticação e (opcionalmente) papéis específicos.
function Protected({ roles }) {
  const { isAuthenticated, user, loading } = useAuthStore();
  if (loading) return null;
  if (!isAuthenticated || !WEB_ROLES.includes(user?.role)) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) {
    return <Navigate to={user?.role === 'admin' ? '/admin' : '/trainer'} replace />;
  }
  return <Outlet />;
}

// Redireciona a raiz conforme o papel.
function RoleHome() {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated || !WEB_ROLES.includes(user?.role)) return <Navigate to="/login" replace />;
  return <Navigate to={user?.role === 'admin' ? '/admin' : '/trainer'} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/register', element: <Register /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  // Páginas públicas (sem login): vitrine do personal, status de compra e convite.
  { path: '/p/:slug', element: <TrainerPage /> },
  { path: '/compra/:id', element: <PurchaseStatus /> },
  { path: '/convite', element: <AcceptInvite /> },
  {
    element: <Protected roles={['admin']} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/admin', element: <AdminDashboard /> },
          { path: '/admin/trainers', element: <AdminTrainers /> },
          { path: '/admin/users', element: <AdminUsers /> },
          { path: '/admin/exercises', element: <AdminExercises /> },
          { path: '/admin/exercises/import', element: <ExerciseImport /> },
          { path: '/admin/equipments', element: <AdminEquipments /> },
          { path: '/admin/equipments/import', element: <EquipmentImport /> },
          { path: '/admin/marketplace', element: <AdminMarketplace /> },
          { path: '/admin/reviews', element: <AdminReviews /> },
          { path: '/admin/coupons', element: <AdminCoupons /> },
          { path: '/admin/finance', element: <AdminFinance /> },
          { path: '/admin/invoices', element: <AdminInvoices /> },
          { path: '/admin/audit', element: <AdminAudit /> },
          { path: '/admin/themes', element: <AdminThemes /> },
          { path: '/admin/settings', element: <Appearance /> },
          { path: '/admin/account', element: <Account /> },
        ],
      },
    ],
  },
  {
    element: <Protected roles={['trainer']} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/trainer', element: <TrainerDashboard /> },
          { path: '/trainer/students', element: <TrainerStudents /> },
          { path: '/trainer/agenda', element: <TrainerAgenda /> },
          { path: '/trainer/library', element: <TrainerLibrary /> },
          { path: '/trainer/equipments', element: <TrainerEquipments /> },
          { path: '/trainer/workouts', element: <TrainerWorkouts /> },
          { path: '/trainer/templates', element: <TrainerTemplates /> },
          { path: '/trainer/packages', element: <TrainerPackages /> },
          { path: '/trainer/chat', element: <TrainerChat /> },
          { path: '/trainer/profile', element: <TrainerProfile /> },
          { path: '/trainer/evolution', element: <TrainerEvolution /> },
          { path: '/trainer/invoices', element: <TrainerInvoices /> },
          { path: '/trainer/settings', element: <Appearance /> },
          { path: '/trainer/account', element: <Account /> },
        ],
      },
    ],
  },
  { path: '/', element: <RoleHome /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);
